/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { useMemo } from 'react';

// Add local dependencies.
import { useGetCurrentContextQuery } from '../features/studios/studiosApi';
import { StudioRole, StudioPermissions } from '../types/studio';
import {
  calculatePermissions,
  hasPermission,
  canManageUser,
  getAssignableRoles,
  isValidRoleChange,
  getRoleDisplayName,
  getRoleColor,
} from '../utils/permissions';

export interface UseStudioPermissionsReturn {
  // Current user's role and permissions
  role: StudioRole | null;
  permissions: StudioPermissions;
  isLoading: boolean;
  error: any;

  // Permission checking functions
  hasPermission: (permission: keyof StudioPermissions) => boolean;
  canManageUser: (targetRole: StudioRole) => boolean;
  canAssignRole: (role: StudioRole) => boolean;
  canChangeRole: (currentRole: StudioRole, newRole: StudioRole) => boolean;

  // Utility functions
  getAssignableRoles: () => StudioRole[];
  getRoleDisplayName: (role: StudioRole) => string;
  getRoleColor: (role: StudioRole) => string;

  // Studio context
  studioId: string | null;
  studioName: string | null;
}

/**
 * Hook for managing studio permissions and role-based access control
 */
export const useStudioPermissions = (): UseStudioPermissionsReturn => {
  const {
    data: context,
    isLoading,
    error,
  } = useGetCurrentContextQuery(undefined, {
    // Refetch when the component mounts or when switching studios
    refetchOnMountOrArgChange: true,
    // Keep data fresh
    refetchOnFocus: true,
  });

  const role = context?.userRole || null;
  const studioId = context?.currentStudio?.id || null;
  const studioName = context?.currentStudio?.name || null;

  // Use backend permissions from context and map to frontend interface
  const permissions = useMemo(() => {
    // If we have backend permissions from context, map them to frontend interface
    if (context?.permissions) {
      const backendPerms = context.permissions as any; // Backend uses snake_case

      // Map backend permission fields to frontend StudioPermissions interface
      const mappedPermissions = {
        canViewStudio: true, // All roles can view studio
        canEditStudio: backendPerms.can_manage_studio || false,
        canDeleteStudio: role === 'owner', // Only owners can delete
        canInviteMembers: backendPerms.can_manage_members || false,
        canViewMembers: true, // All roles can view members list
        canManageMembers: backendPerms.can_manage_members || false,
        canViewBilling: backendPerms.can_manage_studio || false,
        canManageBilling: role === 'owner', // Only owners can manage billing
        canAccessTools: true, // All roles can access tools
        canConfigureTools: backendPerms.can_manage_studio || false,
      };

      return mappedPermissions;
    }
    // Fallback to local calculation only if no backend permissions available
    return calculatePermissions(role);
  }, [context?.permissions, role]);

  // Memoized permission checking functions
  const permissionCheckers = useMemo(
    () => ({
      hasPermission: (permission: keyof StudioPermissions) =>
        hasPermission(permissions, permission),

      canManageUser: (targetRole: StudioRole) =>
        canManageUser(role, targetRole),

      canAssignRole: (targetRole: StudioRole) => {
        const assignableRoles = getAssignableRoles(role);
        return assignableRoles.includes(targetRole);
      },

      canChangeRole: (currentRole: StudioRole, newRole: StudioRole) =>
        isValidRoleChange(role, currentRole, newRole),

      getAssignableRoles: () => getAssignableRoles(role),

      getRoleDisplayName: (roleToDisplay: StudioRole) =>
        getRoleDisplayName(roleToDisplay),

      getRoleColor: (roleToDisplay: StudioRole) => getRoleColor(roleToDisplay),
    }),
    [role, permissions]
  );

  return {
    // Current user's role and permissions
    role,
    permissions,
    isLoading,
    error,

    // Permission checking functions
    ...permissionCheckers,

    // Studio context
    studioId,
    studioName,
  };
};

/**
 * Hook for checking a specific permission
 * Useful for simple permission checks in components
 */
export const usePermission = (
  permission: keyof StudioPermissions
): {
  hasPermission: boolean;
  isLoading: boolean;
  error: any;
} => {
  const {
    hasPermission: checkPermission,
    isLoading,
    error,
  } = useStudioPermissions();

  return {
    hasPermission: checkPermission(permission),
    isLoading,
    error,
  };
};

/**
 * Hook for checking if current user can manage another user
 * Useful for member management components
 */
export const useCanManageUser = (
  targetRole: StudioRole
): {
  canManage: boolean;
  isLoading: boolean;
  error: any;
} => {
  const {
    canManageUser: checkCanManageUser,
    isLoading,
    error,
  } = useStudioPermissions();

  return {
    canManage: checkCanManageUser(targetRole),
    isLoading,
    error,
  };
};

/**
 * Hook for getting role-related utilities
 * Useful for role display and management components
 */
export const useRoleUtils = () => {
  const {
    role,
    getAssignableRoles: getAssignableRolesList,
    getRoleDisplayName: getDisplayName,
    getRoleColor: getColor,
    isLoading,
    error,
  } = useStudioPermissions();

  return {
    currentRole: role,
    getAssignableRoles: getAssignableRolesList,
    getRoleDisplayName: getDisplayName,
    getRoleColor: getColor,
    isLoading,
    error,
  };
};
